import { ref } from 'vue'
import apiClient from '@/api/client'
import { FOLLOW_UPS } from '@/api/endpoints'

interface FollowUp {
  id: string
  subject: string
  recipient_email: string
  recipient_name: string
  sent_at: string
  follow_up_count: number
  next_follow_up_date: string
  business_days_since_sent: number
  next_follow_up_level: string
  follow_up_status: string
  email_id: string
}

interface FollowUpRecommendation {
  email_id: string
  subject: string
  recipient_email: string
  recipient_name: string
  sent_at: string
  follow_up_count: number
  next_follow_up_date: string
  recommended_follow_up_date: string
  business_days_since_sent: number
  has_reply: boolean
  priority_score: number
  recommended_action: string
  urgency_level: string
  status_description: string
  institution_id?: string
  content_id?: string
}

interface FollowUpStatistics {
  total_recommendations: number
  urgency_breakdown: Record<string, number>
  action_breakdown: Record<string, number>
}

export function useFollowUps() {
  const followUps = ref<FollowUp[]>([])
  const recommendations = ref<FollowUpRecommendation[]>([])
  const statistics = ref<FollowUpStatistics | null>(null)
  const paginationInfo = ref({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    page_size: 100
  })
  const loading = ref(false)
  const error = ref<string | null>(null)
  const actionInProgress = ref(false)

  async function fetchFollowUps(institutionId?: string) {
    loading.value = true
    error.value = null
    try {
      const params: any = {}
      if (institutionId) {
        params.institution_id = institutionId
      }
      const response = await apiClient.get(FOLLOW_UPS.LIST, { params })
      followUps.value = response.data.items
      return response.data
    } catch (err: any) {
      console.error('Error fetching follow-ups:', err)
      error.value = err.response?.data?.message || 'Failed to fetch follow-ups'
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function generateFollowUp(emailId: string, customInstructions?: string, forceGeneration?: boolean) {
    actionInProgress.value = true
    error.value = null
    try {
      const payload: any = {}
      if (customInstructions) payload.custom_instructions = customInstructions
      if (forceGeneration) payload.force_generation = true
      
      const response = await apiClient.post(`/api/follow-ups/generate/${emailId}`, payload)
      return response.data
    } catch (err: any) {
      console.error('Error generating follow-up:', err)
      
      // Check if this is the "too recent" error that allows forcing
      if (err.response?.status === 409 && err.response?.data?.detail?.error === 'too_recent') {
        // Return the structured error for the component to handle
        const errorData = err.response.data.detail
        error.value = errorData.message
        throw {
          type: 'too_recent',
          message: errorData.message,
          days_since_last: errorData.days_since_last,
          last_sent_date: errorData.last_sent_date,
          can_force: errorData.can_force,
          email_id: emailId
        }
      }
      
      error.value = err.response?.data?.detail || err.message || 'Failed to generate follow-up'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  async function generateBulkFollowUps(emailIds: string[], followUpLevel?: number, maxEmails: number = -25) {
    actionInProgress.value = true
    error.value = null
    try {
      const payload = {
        email_ids: emailIds,
        follow_up_level: followUpLevel,
        max_emails: maxEmails
      }
      const response = await apiClient.post('/api/follow-ups/generate-bulk', payload)
      return response.data
    } catch (err: any) {
      console.error('Error generating bulk follow-ups:', err)
      error.value = err.response?.data?.detail || err.message || 'Failed to generate bulk follow-ups'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  async function skipFollowUp(emailId: string) {
    actionInProgress.value = true
    error.value = null
    try {
      const response = await apiClient.post(FOLLOW_UPS.SKIP(emailId), {})
      return response.data
    } catch (err: any) {
      console.error('Error skipping follow-up:', err)
      error.value = err.response?.data?.detail || err.message || 'Failed to skip follow-up'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  async function rescheduleFollowUp(emailId: string, followUpDate: string) {
    actionInProgress.value = true
    error.value = null
    try {
      const response = await apiClient.post(FOLLOW_UPS.RESCHEDULE(emailId), { follow_up_date: followUpDate })
      return response.data
    } catch (err: any) {
      console.error('Error rescheduling follow-up:', err)
      error.value = err.response?.data?.detail || err.message || 'Failed to reschedule follow-up'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  async function createManualFollowUp(data: {
    recipients: Array<{ email: string; name?: string }>,
    subject: string,
    body: string,
    content_id?: string,
    institution_id?: string,
    cc_recipients?: string[]
  }) {
    actionInProgress.value = true
    error.value = null
    try {
      const response = await apiClient.post(FOLLOW_UPS.MANUAL, data)
      return response.data
    } catch (err: any) {
      console.error('Error creating manual follow-up:', err)
      error.value = err.response?.data?.detail || err.message || 'Failed to create manual follow-up'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  async function fetchRecommendations(filters: {
    urgency_level?: string
    recommended_action?: string
    institution_id?: string
    min_priority_score?: number
    search?: string
    page?: number
    page_size?: number
  } = {}) {
    loading.value = true
    error.value = null
    try {
      const params = {
        page: filters.page || 1,
        page_size: filters.page_size || 100,
        sort_by: 'priority_score',
        sort_order: 'desc',
        ...filters
      }
      const response = await apiClient.get(FOLLOW_UPS.RECOMMENDATIONS, { params })
      recommendations.value = response.data.items
      statistics.value = response.data.statistics
      
      // Update pagination info
      paginationInfo.value = {
        current_page: response.data.page || 1,
        total_pages: response.data.total_pages || 1,
        total_items: response.data.total || 0,
        page_size: response.data.page_size || 100
      }
      
      return response.data
    } catch (err: any) {
      console.error('Error fetching follow-up recommendations:', err)
      error.value = err.response?.data?.message || 'Failed to fetch recommendations'
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function processBulkRecommendations(options: {
    email_ids?: string[]
    action: 'generate' | 'skip' | 'reschedule'
    max_emails?: number
    urgency_filter?: string
  }) {
    actionInProgress.value = true
    error.value = null
    try {
      const payload = {
        max_emails: 25,
        urgency_filter: 'medium',
        ...options
      }
      const response = await apiClient.post(FOLLOW_UPS.PROCESS_BULK, payload)
      return response.data
    } catch (err: any) {
      console.error('Error processing bulk recommendations:', err)
      error.value = err.response?.data?.detail || err.message || 'Failed to process recommendations'
      throw error.value
    } finally {
      actionInProgress.value = false
    }
  }

  return {
    followUps,
    recommendations,
    statistics,
    paginationInfo,
    loading,
    error,
    actionInProgress,
    fetchFollowUps,
    fetchRecommendations,
    generateFollowUp,
    generateBulkFollowUps,
    processBulkRecommendations,
    skipFollowUp,
    rescheduleFollowUp,
    createManualFollowUp
  }
} 